import React, { useState } from 'react';
import { Box, Typography, Button, Container, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import shieldSearchIcon from '../assets/icons/iconsax-shield-search.svg';
import likeIcon from '../assets/icons/like.svg';
import Footer from '../components/Footer';
import { 
  AutoFixHigh, 
  Speed, 
  Security, 
  CheckCircle, 
  Search,
  LibraryBooks,
  Gavel,
  FindInPage
} from '@mui/icons-material';

const ResearchInfoPage: React.FC = () => {
  const navigate = useNavigate();
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);
  const { isAuthenticated } = useAuth();

  const handleGetStarted = () => {
    const intended = '/dashboard?open=research';
    try { sessionStorage.setItem('intendedPath', intended); } catch {}
    if (isAuthenticated) {
      navigate(intended);
    } else {
      navigate(`/dashboard?requireRegistration=1&authMode=login&intendedPath=${encodeURIComponent(intended)}`);
    }
  };

  const stats = [
    { value: '1000+', label: 'Zákonů a předpisů', icon: <LibraryBooks /> },
    { value: '24/7', label: 'Dostupnost', icon: <Security /> },
    { value: '2025', label: 'Aktuální legislativa', icon: <CheckCircle /> },
    { value: 'AI', label: 'Pokročilá analýza', icon: <Search /> }
  ];

  const features = [
    {
      icon: <Search sx={{ fontSize: 32 }} />,
      title: 'Inteligentní Vyhledávání',
      description: 'AI analyzuje váš dotaz a najde relevantní zákony, vyhlášky a judikáty napříč celou legislativou.',
      benefit: 'Přesné výsledky'
    },
    {
      icon: <Speed sx={{ fontSize: 32 }} />,
      title: 'Rychlé Odpovědi',
      description: 'Získejte právní informace během několika sekund místo hodin strávených manuálním hledáním.',
      benefit: 'Úspora času'
    },
    {
      icon: <LibraryBooks sx={{ fontSize: 32 }} />,
      title: 'Kompletní Databáze',
      description: 'Přístup k celé české legislativě včetně zákonů, vyhlášek, nařízení a soudních rozhodnutí.',
      benefit: 'Vše na jednom místě'
    },
    {
      icon: <AutoFixHigh sx={{ fontSize: 32 }} />,
      title: 'Kontextová Analýza',
      description: 'AI poskytuje nejen odkazy na zákony, ale také vysvětlení a praktické dopady pro váš případ.',
      benefit: 'Srozumitelné vysvětlení'
    }
  ];

  const researchAreas = [
    {
      title: 'Občanské Právo',
      icon: <Gavel sx={{ fontSize: 48 }} />,
      description: 'Občanský zákoník, vlastnictví, závazky, dědictví a další oblasti občanského práva.',
      topics: ['Občanský zákoník', 'Vlastnické právo', 'Závazkové právo', 'Dědické právo'],
      color: '#E8F5E8'
    },
    {
      title: 'Obchodní Právo',
      icon: <FindInPage sx={{ fontSize: 48 }} />,
      description: 'Zákon o obchodních korporacích, živnostenský zákon a další předpisy pro podnikání.',
      topics: ['Obchodní korporace', 'Živnostenské právo', 'Konkurence', 'Insolvence'],
      color: '#FFF3E0'
    },
    {
      title: 'Správní Právo',
      icon: <LibraryBooks sx={{ fontSize: 48 }} />,
      description: 'Správní řád, stavební zákon, daňové předpisy a další správní legislativa.',
      topics: ['Správní řízení', 'Stavební právo', 'Daňové právo', 'Přestupky'],
      color: '#E3F2FD'
    }
  ];

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100vh',
      backgroundColor: '#F2F2F2',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <Container maxWidth={false} sx={{ 
        maxWidth: '1920px',
        px: { xs: 2, sm: 3, md: 4, lg: '277px' }
      }}>
        {/* Hero Section */}
        <Box sx={{
          position: 'relative',
          mt: { xs: '100px', sm: '120px', md: '132px' },
          mb: { xs: 8, sm: 10, md: 12 }
        }}>
          {/* Enhanced Gradient Background */}
          <Box sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '400%', sm: '250%', md: '180%', lg: '1600px' },
            height: { xs: '400px', sm: '450px', md: '500px' },
            borderRadius: '50%',
            background: 'radial-gradient(circle at 50% 50%, rgba(100, 100, 255, 0.1) 0%, rgba(255, 100, 100, 0.05) 30%, rgba(128, 128, 128, 0.3) 60%, rgba(237, 237, 237, 0) 100%)',
            filter: 'blur(120px)',
            zIndex: 0
          }} />
          
          <Box sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: { xs: 3, sm: 4, md: 5 },
            py: { xs: 6, sm: 7, md: 8 },
            zIndex: 1
          }}>

            <Box 
              component="img" 
              src={shieldSearchIcon} 
              alt="Právní výzkum" 
              sx={{ 
                width: { xs: '80px', sm: '100px', md: '120px' },
                height: { xs: '80px', sm: '100px', md: '120px' },
                filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.1))'
              }} 
            />
            
            <Typography sx={{ 
              fontFamily: 'Inter',
              fontWeight: 800,
              fontSize: { xs: '32px', sm: '42px', md: '52px', lg: '64px' },
              lineHeight: { xs: 1.1, md: 1.1 },
              textAlign: 'center',
              color: '#000000',
              maxWidth: { xs: '100%', sm: '700px', md: '900px', lg: '1100px' },
              background: 'linear-gradient(135deg, #1F1F1F 0%, #666666 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              px: { xs: 2, sm: 3 }
            }}>
              Rychlejší výzkum ={' '}
              <Box component="span" sx={{ 
                background: 'linear-gradient(135deg, #e72f0d 0%, #e72f0d 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                silnější argumentace
              </Box>
            </Typography>

            <Typography sx={{ 
              fontFamily: 'Inter',
              fontWeight: 400,
              fontSize: { xs: '16px', sm: '18px', md: '20px' },
              lineHeight: 1.5,
              textAlign: 'center',
              color: '#666666',
              maxWidth: '600px',
              px: { xs: 2, sm: 3 }
            }}>
              Nalezněte relevantní precedenty za 30 sekund místo hodin hledání. <strong>Ušetříte 3-4 hodiny výzkumu na každý případ.</strong>
            </Typography>
            
            <Box sx={{ 
              display: 'flex',
              gap: { xs: 2, sm: 3 },
              flexWrap: 'wrap',
              justifyContent: 'center',
              mt: 2
            }}>
              <Button
                onClick={handleGetStarted}
                size="large"
                sx={{
                  padding: { xs: '14px 28px', sm: '16px 32px' },
                  bgcolor: '#1F1F1F !important',
                  backgroundColor: '#1F1F1F !important',
                  background: '#1F1F1F !important',
                  color: '#FFFFFF !important',
                  fontSize: { xs: '14px', sm: '16px' },
                  fontWeight: 600,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  border: 'none !important',
                  boxShadow: '0 4px 12px rgba(31, 31, 31, 0.15)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: '#e72f0d !important',
                    backgroundColor: '#e72f0d !important',
                    background: '#e72f0d !important',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(231, 47, 13, 0.25)'
                  }
                }}
              >
                Začít výzkum
              </Button>
              
            </Box>

            {/* Jednoduchý návod – Právní výzkum */}
            <Box sx={{
              mt: { xs: 3, sm: 4 },
              p: { xs: 2.5, sm: 3 },
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.06)',
              border: '1px solid rgba(0,0,0,0.06)',
              maxWidth: 900,
              mx: 'auto'
            }}>
              <Typography sx={{ fontFamily: 'Inter', fontWeight: 700, fontSize: { xs: '20px', sm: '22px' }, color: '#1F1F1F', textAlign: 'center', mb: 1.5 }}>
                Jak začít.
              </Typography>
              <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: { xs: '14px', sm: '15px' }, color: '#666', textAlign: 'center', mb: 2.5 }}>
                Po kliknutí na „Začít výzkum“ budete přesměrováni na Dashboard. Tam proběhne přihlášení/registrace a odkud ovládáte všechny funkce.
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <CheckCircle sx={{ color: '#10b981', mt: '2px' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '15px', sm: '16px' } }}>1) Zadejte dotaz</Typography>
                    <Typography sx={{ color: '#555', fontSize: { xs: '14px', sm: '15px' } }}>V Dashboardu otevřete „Právní výzkum“ a napište, co potřebujete najít (zákon, judikát, téma).</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <CheckCircle sx={{ color: '#10b981', mt: '2px' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '15px', sm: '16px' } }}>2) Prohlédněte si výsledky</Typography>
                    <Typography sx={{ color: '#555', fontSize: { xs: '14px', sm: '15px' } }}>Dostanete přehled s odkazy na zdroje a srozumitelným shrnutím.</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <CheckCircle sx={{ color: '#10b981', mt: '2px' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '15px', sm: '16px' } }}>3) Uložte si, co potřebujete</Typography>
                    <Typography sx={{ color: '#555', fontSize: { xs: '14px', sm: '15px' } }}>Vše najdete v Dashboardu a můžete se k tomu vracet.</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <CheckCircle sx={{ color: '#10b981', mt: '2px' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '15px', sm: '16px' } }}>4) Vše na jednom místě</Typography>
                    <Typography sx={{ color: '#555', fontSize: { xs: '14px', sm: '15px' } }}>Dashboard je vaše „centrála“ – rychlý přístup ke všem funkcím i historii.</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

          </Box>
        </Box>

        {/* Stats Section */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: { xs: 2, sm: 3 },
          mb: { xs: 8, sm: 10, md: 12 }
        }}>
          {stats.map((stat, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                padding: { xs: '24px 16px', sm: '32px 20px' },
                backgroundColor: 'rgba(255,255,255,0.9)',
                backdropFilter: 'blur(10px)',
                borderRadius: '20px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                transition: 'all 0.5s ease-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
                }
              }}
            >
              <Box sx={{ color: '#1F1F1F', mb: 1 }}>
                {stat.icon}
              </Box>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 700,
                fontSize: { xs: '24px', sm: '28px' },
                color: '#1F1F1F'
              }}>
                {stat.value}
              </Typography>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 500,
                fontSize: { xs: '12px', sm: '14px' },
                color: '#666666',
                textAlign: 'center'
              }}>
                {stat.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Features Section */}
        <Box sx={{
          position: 'relative',
          mb: { xs: 8, sm: 10, md: 12 }
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: { xs: 4, sm: 5, md: 6 },
            mb: { xs: 4, sm: 6 }
          }}>
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 700,
              fontSize: { xs: '28px', sm: '36px', md: '42px' },
              lineHeight: 1.2,
              textAlign: 'center',
              color: '#1F1F1F',
              maxWidth: '800px'
            }}>
              Proč používat AI právní výzkum?
            </Typography>
            
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: { xs: 3, sm: 4 },
              width: '100%'
            }}>
              {features.map((feature, index) => (
                <Box
                  key={index}
                  onMouseEnter={() => setHoveredFeature(index)}
                  onMouseLeave={() => setHoveredFeature(null)}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    padding: { xs: '32px 24px', sm: '40px 32px' },
                    backgroundColor: hoveredFeature === index 
                      ? 'rgba(255,255,255,1)' 
                      : 'rgba(255,255,255,0.9)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '24px',
                    boxShadow: hoveredFeature === index 
                      ? '0 12px 40px rgba(0,0,0,0.15)' 
                      : '0 4px 20px rgba(0,0,0,0.08)',
                    transition: 'all 0.5s ease-out',
                    cursor: 'pointer',
                    transform: hoveredFeature === index ? 'translateY(-8px)' : 'translateY(0)',
                    border: hoveredFeature === index ? '2px solid rgba(231,47,13,0.2)' : '2px solid transparent'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ 
                      color: hoveredFeature === index ? '#e72f0d' : '#1F1F1F',
                      transition: 'color 0.4s ease-out'
                    }}>
                      {feature.icon}
                    </Box>
                    <Chip 
                      label={feature.benefit}
                      size="small"
                      sx={{ 
                        backgroundColor: hoveredFeature === index ? '#e72f0d' : '#E8F5E8',
                        color: hoveredFeature === index ? 'white' : '#2E7D32',
                        fontWeight: 600,
                        fontSize: '11px'
                      }}
                    />
                  </Box>
                  
                  <Box>
                    <Typography sx={{
                      fontFamily: 'Inter',
                      fontWeight: 600,
                      fontSize: { xs: '20px', sm: '22px' },
                      color: '#1F1F1F',
                      mb: 1
                    }}>
                      {feature.title}
                    </Typography>
                    <Typography sx={{
                      fontFamily: 'Inter',
                      fontWeight: 400,
                      fontSize: { xs: '14px', sm: '15px' },
                      lineHeight: 1.6,
                      color: '#666666'
                    }}>
                      {feature.description}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Research Areas Grid */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: 'repeat(3, 1fr)' 
          },
          gap: { xs: 3, sm: 4 },
          mb: { xs: 8, sm: 10, md: 12 }
        }}>
          {researchAreas.map((area, index) => (
            <Box 
              key={index}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: { xs: 3, sm: 4 },
                padding: { xs: '32px 24px', sm: '40px 32px' },
                backgroundColor: '#FFFFFF',
                borderRadius: '24px',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
                transition: 'all 0.5s ease-out',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)'
                }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ color: '#1F1F1F' }}>
                  {area.icon}
                </Box>
                <Typography sx={{
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: { xs: '22px', sm: '24px' },
                  lineHeight: 1.3,
                  color: '#1F1F1F'
                }}>
                  {area.title}
                </Typography>
                <Typography sx={{
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontSize: { xs: '14px', sm: '15px' },
                  lineHeight: 1.5,
                  color: '#666666'
                }}>
                  {area.description}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  {area.topics.map((topic, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircle sx={{ fontSize: 16, color: '#4CAF50', mr: 1 }} />
                      <Typography sx={{
                        fontFamily: 'Inter',
                        fontWeight: 400,
                        fontSize: '13px',
                        color: '#666666'
                      }}>
                        {topic}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Chip 
                  label="AI vyhledávání" 
                  size="small" 
                  sx={{ 
                    alignSelf: 'flex-start',
                    backgroundColor: area.color,
                    color: '#1F1F1F',
                    fontWeight: 600
                  }}
                />
              </Box>
              <Button
                onClick={handleGetStarted}
                sx={{
                  width: '100%',
                  padding: { xs: '14px 16px', sm: '16px 20px' },
                  color: '#1F1F1F',
                  borderColor: '#1F1F1F',
                  fontSize: { xs: '14px', sm: '15px' },
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(31, 31, 31, 0.08)',
                    borderColor: '#1F1F1F'
                  }
                }}
              >
                Začít výzkum →
              </Button>
            </Box>
          ))}
        </Box>

        {/* Professional Warning */}
        <Box sx={{
          position: 'relative',
          mb: { xs: 6, sm: 8, md: 10 }
        }}>
          <Box sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
            padding: { xs: '16px', sm: '20px' },
            backgroundColor: '#4C4C4C',
            borderRadius: '12px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
            zIndex: 1
          }}>
            <Box 
              component="img" 
              src={likeIcon} 
              alt="Like" 
              sx={{ 
                width: { xs: '20px', sm: '24px' },
                height: { xs: '20px', sm: '24px' }
              }} 
            />
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 500,
              fontSize: { xs: '12px', sm: '14px' },
              lineHeight: 1.5,
              textAlign: 'center',
              color: '#DCDCDC'
            }}>
              Profesionální upozornění: AI výzkum poskytuje právní informace a odkazy na legislativu. Pro konkrétní právní záležitosti konzultujte kvalifikovaného advokáta.
            </Typography>
          </Box>
        </Box>

        {/* Final CTA */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: { xs: 4, sm: 5 },
          padding: { xs: '48px 32px', sm: '64px 40px' },
          background: 'linear-gradient(135deg, #1F1F1F 0%, #333333 100%)',
          borderRadius: '24px',
          boxShadow: '0px 20px 60px rgba(0, 0, 0, 0.25)',
          mb: { xs: 6, sm: 8, md: 10 }
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 700,
              fontSize: { xs: '32px', sm: '42px', md: '48px' },
              lineHeight: 1.1,
              color: '#FFFFFF',
              mb: 3
            }}>
              Připraveni najít odpověď <br />
              rychleji než konkurence?
            </Typography>
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 400,
              fontSize: { xs: '16px', sm: '18px' },
              lineHeight: 1.5,
              color: '#B0B0B0',
              maxWidth: '600px'
            }}>
              Prohledávejte zákony, vyhlášky a judikáty s pomocí AI technologie během několika sekund
            </Typography>
          </Box>
          
          <Box sx={{
            display: 'flex',
            gap: { xs: 2, sm: 3 },
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            <Button 
              onClick={handleGetStarted}
              size="large"
              sx={{
                padding: { xs: '16px 32px', sm: '20px 40px' },
                backgroundColor: '#FFFFFF',
                color: '#1F1F1F',
                borderRadius: '12px',
                fontSize: { xs: '16px', sm: '18px' },
                fontWeight: 700,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                boxShadow: '0 8px 32px rgba(255,255,255,0.2)',
                '&:hover': { 
                  backgroundColor: '#F0F0F0',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 40px rgba(255,255,255,0.3)'
                }
              }}
            >
              Spustit právní výzkum
            </Button>
            <Button 
              onClick={() => navigate('/')}
              variant="outlined"
              sx={{
                padding: { xs: '16px 32px', sm: '20px 40px' },
                border: '2px solid #666666',
                color: '#FFFFFF',
                backgroundColor: 'transparent',
                borderRadius: '12px',
                fontSize: { xs: '16px', sm: '18px' },
                fontWeight: 600,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                '&:hover': { 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderColor: '#FFFFFF'
                }
              }}
            >
              Zobrazit všechny služby
            </Button>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            opacity: 0.8
          }}>
            <Typography sx={{ 
              fontSize: '14px', 
              color: '#B0B0B0',
              fontWeight: 500
            }}>
              • Registrace zdarma  • Kredity dle ceníku  • Okamžitý přístup
            </Typography>
          </Box>
        </Box>
      </Container>

      {/* Footer */}
      <Footer />

      {/* Auth modal now handled on Dashboard after CTA redirect */}
    </Box>
  );
};

export default ResearchInfoPage;
